import '../styles/GlobalDesign.css';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const AdminRevenue = ({ receiptsOnly = false }) => {
  const [dailyRevenue, setDailyRevenue] = useState([]);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [payments, setPayments] = useState([]);
  const [revenueStats, setRevenueStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('daily'); // 'daily' ou 'monthly'
  const [days, setDays] = useState(30);
  const [months, setMonths] = useState(12);
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [paymentLimit, setPaymentLimit] = useState(50);

  useEffect(() => {
    if (receiptsOnly) {
      setLoading(false);
      return;
    }

    loadRevenue();
    loadRevenueStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, days, months, receiptsOnly]);

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus, paymentLimit]);

  const loadRevenue = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (view === 'daily') {
        const response = await axios.get(`${API_URL}/admin/revenue/daily?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDailyRevenue(response.data.dailyRevenue);
      } else {
        const response = await axios.get(`${API_URL}/admin/revenue/monthly?months=${months}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMonthlyRevenue(response.data.monthlyRevenue);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement revenus:', error);
      setLoading(false);
    }
  };

  const loadRevenueStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/revenue/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRevenueStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats revenue:', error);
    }
  };

  const loadPayments = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ limit: String(paymentLimit) });
      if (paymentStatus !== 'all') params.set('status', paymentStatus);
      const response = await axios.get(`${API_URL}/admin/payments?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data.payments || []);
    } catch (error) {
      console.error('Erreur chargement paiements:', error);
      setPayments([]);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatMonth = (monthString) => {
    const [year, month] = monthString.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long'
    });
  };

  const getTotalRevenue = () => {
    if (view === 'daily') {
      return dailyRevenue.reduce((sum, item) => sum + (item.total_revenue || 0), 0);
    }
    return monthlyRevenue.reduce((sum, item) => sum + (item.total_revenue || 0), 0);
  };

  const getTotalPayments = () => {
    if (view === 'daily') {
      return dailyRevenue.reduce((sum, item) => sum + (item.payment_count || 0), 0);
    }
    return monthlyRevenue.reduce((sum, item) => sum + (item.payment_count || 0), 0);
  };

  const isPaidPayment = (status) => {
    const value = String(status || '').toLowerCase();
    return ['paid', 'success', 'successful', 'succeeded', 'completed', 'complete', 'approved', 'valid', 'validated'].includes(value);
  };

  const formatPaymentDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openReceipt = (tokenPay) => {
    if (!tokenPay) return;
    window.open(`/api/payments/receipt/${encodeURIComponent(tokenPay)}?download=1`, '_blank', 'noopener,noreferrer');
  };

  const renderPaymentsSection = (standalone = false) => (
    <>
      <div id="admin-receipts" className={`page-header payments-header ${standalone ? 'admin-receipts-page-header' : ''}`}>
        <h1>{standalone ? 'Reçus' : 'Reçus et paiements récents'}</h1>
        <div className="filters admin-inline-filters">
          <div className="filter-group">
            <label>Statut:</label>
            <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
              <option value="paid">Payés</option>
              <option value="pending">En attente</option>
              <option value="failure">Échoués</option>
              <option value="all">Tous</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Nombre:</label>
            <select value={paymentLimit} onChange={(e) => setPaymentLimit(e.target.value)}>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </div>
        </div>
      </div>

      <div className="revenue-table payments-table">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Client</th>
              <th>Email</th>
              <th>Plan</th>
              <th>Montant</th>
              <th>Statut</th>
              <th>Référence</th>
              <th>Reçu</th>
            </tr>
          </thead>
          <tbody>
            {payments.length > 0 ? (
              payments.map((payment) => (
                <tr key={payment.id || payment.token_pay}>
                  <td>{formatPaymentDate(payment.updated_at || payment.created_at)}</td>
                  <td>{payment.nom || '-'}</td>
                  <td className="admin-text-break">{payment.email || '-'}</td>
                  <td>{payment.plan_type || '-'}</td>
                  <td className="amount">{Number(payment.montant || 0).toLocaleString('fr-FR')} FCFA</td>
                  <td>
                    <span className={`status-badge ${isPaidPayment(payment.statut) ? 'active' : 'expired'}`}>
                      {payment.statut || '-'}
                    </span>
                  </td>
                  <td className="admin-mono">
                    {payment.token_pay ? `${payment.token_pay.substring(0, 12)}...` : '-'}
                  </td>
                  <td>
                    {isPaidPayment(payment.statut) ? (
                      <button className="admin-receipt-btn" onClick={() => openReceipt(payment.token_pay)}>
                        Télécharger
                      </button>
                    ) : (
                      <span className="admin-muted">Indisponible</span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                  Aucun paiement trouvé
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading">Chargement...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-container">
        {receiptsOnly ? (
          renderPaymentsSection(true)
        ) : (
          <>
        <div className="page-header">
          <h1>📊 Statistiques de Revenu</h1>
        </div>

        {/* Statistiques globales */}
        <div className="stats-grid">
          <div className="stat-card today">
            <div className="stat-icon">📅</div>
            <div className="stat-info">
              <div className="stat-value">{revenueStats?.today?.revenue || 0} FCFA</div>
              <div className="stat-label">Revenu Aujourd'hui</div>
              <div className="stat-detail">{revenueStats?.today?.count || 0} paiements</div>
            </div>
          </div>
          <div className="stat-card month">
            <div className="stat-icon">📆</div>
            <div className="stat-info">
              <div className="stat-value">{revenueStats?.month?.revenue || 0} FCFA</div>
              <div className="stat-label">Revenu Ce Mois</div>
              <div className="stat-detail">{revenueStats?.month?.count || 0} paiements</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💰</div>
            <div className="stat-info">
              <div className="stat-value">{getTotalRevenue()} FCFA</div>
              <div className="stat-label">Revenu Total (période)</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">💳</div>
            <div className="stat-info">
              <div className="stat-value">{getTotalPayments()}</div>
              <div className="stat-label">Paiements (période)</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📈</div>
            <div className="stat-info">
              <div className="stat-value">{getTotalPayments() > 0 ? Math.round(getTotalRevenue() / getTotalPayments()) : 0} FCFA</div>
              <div className="stat-label">Moyenne par paiement</div>
            </div>
          </div>
        </div>

        {/* Sélection de la vue */}
        <div className="view-selector">
          <button 
            className={view === 'daily' ? 'active' : ''}
            onClick={() => setView('daily')}
          >
            📅 Par Jour
          </button>
          <button 
            className={view === 'monthly' ? 'active' : ''}
            onClick={() => setView('monthly')}
          >
            📆 Par Mois
          </button>
        </div>

        {/* Filtres */}
        <div className="filters">
          {view === 'daily' ? (
            <div className="filter-group">
              <label>Derniers jours:</label>
              <select value={days} onChange={(e) => setDays(e.target.value)}>
                <option value="7">7 jours</option>
                <option value="15">15 jours</option>
                <option value="30">30 jours</option>
                <option value="60">60 jours</option>
                <option value="90">90 jours</option>
              </select>
            </div>
          ) : (
            <div className="filter-group">
              <label>Derniers mois:</label>
              <select value={months} onChange={(e) => setMonths(e.target.value)}>
                <option value="3">3 mois</option>
                <option value="6">6 mois</option>
                <option value="12">12 mois</option>
                <option value="24">24 mois</option>
              </select>
            </div>
          )}
        </div>

        {/* Tableau des revenus */}
        <div className="revenue-table">
          <table>
            <thead>
              <tr>
                <th>{view === 'daily' ? 'Date' : 'Mois'}</th>
                <th>Nombre de paiements</th>
                <th>Revenu (FCFA)</th>
                <th>Moyenne (FCFA)</th>
              </tr>
            </thead>
            <tbody>
              {view === 'daily' ? (
                dailyRevenue.length > 0 ? (
                  dailyRevenue.map((item, index) => (
                    <tr key={index}>
                      <td>{formatDate(item.date)}</td>
                      <td>{item.payment_count}</td>
                      <td className="amount">{item.total_revenue} FCFA</td>
                      <td>{Math.round(item.total_revenue / item.payment_count)} FCFA</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem' }}>
                      Aucun revenu pour cette période
                    </td>
                  </tr>
                )
              ) : (
                monthlyRevenue.length > 0 ? (
                  monthlyRevenue.map((item, index) => (
                    <tr key={index}>
                      <td>{formatMonth(item.month)}</td>
                      <td>{item.payment_count}</td>
                      <td className="amount">{item.total_revenue} FCFA</td>
                      <td>{Math.round(item.total_revenue / item.payment_count)} FCFA</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem' }}>
                      Aucun revenu pour cette période
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>

        {renderPaymentsSection(false)}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminRevenue;
